<template>
  <v-card color="#191919">
    <v-card-subtitle class="subtitle-1 font-weight-bold">{{ $tc('kommentti.title', 2) }}</v-card-subtitle>
    <v-divider />
    <v-card-text>
      <v-list :class="kommentit.length === 0 ? 'pa-0' : ''" color="transparent">
        <v-list-item v-if="kommentit.length === 0" class="ma-0 pa-0">
          <v-alert class="mb-0" width="100%" outlined text type="info">{{ $t('kommentti.ei_kommentteja') }}</v-alert>
        </v-list-item>
        <template v-for="(kommentti, index) in kommentit">
          <v-list-item dense :key="kommentti.id" class="mt-2" three-line>
            <v-list-item-avatar>
              <v-avatar v-if="kommentti.user" color="primary" size="56">
                <span class="white--text">{{ getInitials(kommentti) }}</span>
              </v-avatar>
            </v-list-item-avatar>
            <v-list-item-content>
              <v-list-item-title v-if="kommentti.user"> {{ kommentti.user.firstname }} {{ kommentti.user.lastname }} </v-list-item-title>

              <div class="my-2" v-if="!kommentti.editing" style="word-break: break-word; white-space: pre-wrap">
                {{ kommentti.kommenttiTeksti }}
                <v-btn
                  v-if="kommentti.userId === $user.id && !kommentti.editing"
                  icon
                  style="top: -2px; left: 5px"
                  small
                  class=""
                  @click="editKommentti(kommentti)"
                >
                  <v-icon size="20" color="primary" class="">mdi mdi-pencil</v-icon>
                </v-btn>
              </div>
              <v-textarea
                v-model="kommentti.kommenttiTekstiEdit"
                v-if="$vuetify.breakpoint.smAndUp && kommentti.editing"
                :disabled="saving"
                auto-grow
                class="my-1 pa-0"
                rows="1"
                hide-details
                @keydown.enter.exact.prevent="updateComment(kommentti)"
                v-on:keydown.esc="closeKommenttiEdit(kommentti)"
                style="font-size: 0.875rem; font-weight: 400"
                type="text"
                validate-on-blur
              >
                <template v-slot:append-outer>
                  <v-btn
                    :disabled="kommentti.kommenttiTekstiEdit.length === 0"
                    color="primary"
                    icon
                    small
                    :loading="saving"
                    @click="updateComment(kommentti)"
                  >
                    <v-icon>mdi mdi-check</v-icon>
                  </v-btn>
                  <v-btn color="primary" icon small :loading="saving" @click="closeKommenttiEdit(kommentti)">
                    <v-icon>mdi mdi-close</v-icon>
                  </v-btn>
                </template>
              </v-textarea>
              <v-list-item-subtitle class="caption" :style="kommentti.editing && { marginTop: '2.5px' }">{{
                $formatDateTime(kommentti.aika)
              }}</v-list-item-subtitle>
            </v-list-item-content>
            <v-list-item-action>
              <v-btn
                v-if="$hasAdminPermission() || kommentti.userId === $user.id"
                :loading="kommentti._deleting"
                icon
                @click="deleteKommentti(kommentti)"
              >
                <v-icon color="red">mdi mdi-delete</v-icon>
              </v-btn>
            </v-list-item-action>
          </v-list-item>
          <v-list-item
            v-if="$vuetify.breakpoint.xsOnly && kommentti.editing"
            :key="kommentti.id + '_edit_field'"
            :class="kommentti.editing && $vuetify.breakpoint.xsOnly ? 'pb-2' : ''"
          >
            <v-textarea
              v-model="kommentti.kommenttiTekstiEdit"
              :disabled="saving"
              auto-grow
              :id="'kommentti_edit_field_' + kommentti.id"
              class="my-1 pa-0"
              rows="1"
              hide-details
              @keydown.enter.exact.prevent="updateComment(kommentti)"
              v-on:keydown.esc="closeKommenttiEdit(kommentti)"
              style="font-size: 0.875rem; font-weight: 400"
              type="text"
              validate-on-blur
            >
              <template v-slot:append-outer>
                <v-btn
                  :disabled="kommentti.kommenttiTekstiEdit.length === 0"
                  color="primary"
                  icon
                  small
                  :loading="saving"
                  @click="updateComment(kommentti)"
                >
                  <v-icon>mdi mdi-check</v-icon>
                </v-btn>
                <v-btn color="primary" icon small :loading="saving" @click="closeKommenttiEdit(kommentti)">
                  <v-icon>mdi mdi-close</v-icon>
                </v-btn>
              </template>
            </v-textarea>
          </v-list-item>
          <v-divider v-if="index !== kommentit.length - 1" :key="`divider_${kommentti.id}`" />
        </template>
      </v-list>
    </v-card-text>
    <v-divider></v-divider>
    <v-card-text>
      <v-textarea
        v-model="edit.kommenttiTeksti"
        :disabled="saving"
        :label="$t('kommentti.teksti')"
        auto-grow
        rows="1"
        @keydown.enter.exact.prevent="save"
        type="text"
        validate-on-blur
      >
        <template v-slot:append-outer>
          <v-btn
            v-if="$vuetify.breakpoint.smAndUp"
            :disabled="!edit.kommenttiTeksti"
            color="primary"
            icon
            small
            :loading="saving"
            @click="save"
          >
            <v-icon size="30" class="mb-2">mdi mdi-send</v-icon>
          </v-btn>
        </template>
      </v-textarea>
    </v-card-text>
    <template v-if="$vuetify.breakpoint.xsOnly">
      <v-divider />
      <v-card-actions class="px-4">
        <v-btn color="primary" large block :loading="saving" @click="save">
          <v-icon left>mdi mdi-plus</v-icon>
          {{ $t('kommentti.add_kommentti') }}
        </v-btn>
      </v-card-actions>
    </template>
  </v-card>
</template>

<script>
import api from '../apicollection';

export default {
  name: 'Kommentit',
  props: {
    tehtavaId: Number,
    valintaId: Number,
  },
  data() {
    return {
      kommentit: [],
      edit: {},
      saving: false,
    };
  },
  methods: {
    getInitials(kommentti) {
      if (kommentti.user) {
        return [kommentti.user?.firstname?.split('')?.[0], kommentti.user?.lastname?.split('')?.[0]].map(s => s.toUpperCase()).join('');
      }
      return null;
    },
    async save() {
      const kommentti = { ...this.edit, valintaId: this.valintaId, tehtavaId: this.tehtavaId };
      if (!kommentti.kommenttiTeksti || kommentti.kommenttiTeksti.length === 0) {
        return;
      }
      this.saving = true;
      try {
        await api.createKommentti(kommentti);
        this.edit = {};
        await this.getKommentit();
      } catch (e) {
        this.$showErrorNotification(this.$t('errors.error_saving_comment'));
      } finally {
        this.saving = false;
      }
    },
    async getKommentit() {
      let body = {};
      if (this.tehtavaId) {
        body = { tehtavaId: this.tehtavaId };
      } else if (this.valintaId) {
        body = { valintaId: this.valintaId };
      }
      const kommentit = await api.getKommentit(body);
      this.kommentit = kommentit;
    },
    editKommentti(kommentti) {
      const index = this.kommentit.findIndex(object => {
        return object.id === kommentti.id;
      });
      const kommentitCopy = [...this.kommentit];
      kommentitCopy[index].editing = true;
      kommentitCopy[index].kommenttiTekstiEdit = kommentitCopy[index].kommenttiTeksti;

      this.kommentit = kommentitCopy;
    },
    async updateComment(kommentti) {
      const comment = {
        aika: kommentti.aika,
        id: kommentti.id,
        kommenttiTeksti: kommentti.kommenttiTekstiEdit,
        tehtava: kommentti.tehtava,
        tehtavaId: kommentti.tehtavaId,
        user: kommentti.user,
        userId: kommentti.userId,
        valinta: kommentti.valinta,
        valintaId: kommentti.valintaId,
      };
      try {
        await api.updateKommentti(comment);
        this.$showSuccessNotification(this.$t('kommentti.succesfully_saved'), false);
      } catch (e) {
        console.log(e);
        this.$showErrorNotification(this.$t('kommentti.error_in_kommentti_saving'), false);
      }
      await this.getKommentit();
    },
    closeKommenttiEdit(kommentti) {
      const index = this.kommentit.findIndex(object => {
        return object.id === kommentti.id;
      });
      const kommentitCopy = [...this.kommentit];

      kommentitCopy[index].editing = false;
      this.kommentit = kommentitCopy;
    },
    async deleteKommentti(kommentti) {
      await this.$root
        .$confirm(this.$t('kommentti.delete_dialog.title'), this.$t('kommentti.delete_dialog.description'), {
          color: 'error',
          agree: this.$t('kommentti.delete_dialog.agree'),
        })
        .then(async confirm => {
          if (confirm) {
            const idx = this.kommentit.findIndex(k => k.id === kommentti.id);
            if (idx > -1) {
              this.$set(this.kommentit, idx, { ...kommentti, _deleting: true });
            }
            try {
              await api.deleteKommentti(kommentti);
              this.$showSuccessNotification(this.$t('kommentti.succesfully_deleted'), false);
            } catch (e) {
              this.$showErrorNotification(this.$t('kommentti.error_in_kommentti_deleting'), false);
            }
            if (idx > -1) {
              this.$set(this.kommentit, idx, { ...kommentti, _deleting: false });
            }
            await this.getKommentit();
          }
        });
    },
  },
  async mounted() {
    await Promise.all([this.getKommentit()]);
  },
};
</script>

<style scoped></style>
